// Один экран на все смарт-процессы: счета клиенту (31), платёжный календарь (1160),
// реестр договоров (153), база АВР (170), отчёты по сделкам (176).
//
// ПОЧЕМУ ОДИН, А НЕ ПЯТЬ. Читаются они совершенно одинаково — `crm.item.list` с другим
// `entityTypeId`, та же постраничность по 50, те же системные поля (стадия, воронка,
// компания, сделка, ответственный). Различается ТОЛЬКО состав UF-полей, а его нам всё равно
// нельзя зашивать: коды у каждого типа свои (`ufCrm22_` у 134, `ufCrm18_` у 170, `ufCrm13_`
// у 176), их меняют в Bitrix без нас, и написанный руками список полей начал бы врать
// в первый же месяц. Поэтому колонки и фильтры строятся по справочнику `/fields`, а разница
// между типами сведена к строке в SMART_TYPES.
//
// Дебиторка (134) осталась отдельным экраном намеренно: там не список полей, а РАСЧЁТ —
// что считать оплаченным, как считать остаток и корзину срочности. Этот расчёт из справочника
// не выводится, он выяснен по трём порталам и живёт в lib/payments.ts. Загнать его сюда
// значило бы сделать общий экран, который на 90% состоит из исключения для одного типа.
//
// Экран ТОЛЬКО ЧИТАЕТ, и это не «пока не успели»: почти все эти типы наполняют бизнес-процессы
// Bitrix (`БИЗНЕС-ПРОЦЕССЫ_Bitrix.md`), запись в них перезапускает согласования и роботов.

import { useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useEntityTitle, useFields, useFieldsQuery, useItems, useRefs } from '../api/hooks'
import { оговоркаИсточника } from '../api/smartOwn'
import type { ListParams } from '../api/types'
import { EntityPicker } from '../components/EntityPicker'
import { FilterBar } from '../components/FilterBar'
import { NAV_GROUPS } from '../components/nav'
import { QueryState } from '../components/QueryState'
import { SmartItemsTable } from '../components/SmartItemsTable'
import type { EntityRef } from '../lib/entities'
import { fmtMoney, plural, pluralN } from '../lib/format'
import type { FilterMode, SmartTypeSpec } from '../lib/smart'
import {
  FILTER_MODE_LABEL, SMART_TYPES, SYSTEM_SELECT, buildColumns, fieldFilter, filterModes,
  kindOfFieldType, moneyTotals, smartEntity, smartTypeByPath, sortableColumns,
} from '../lib/smart'

const PAGE = 50   // страница ядра (PAGE в core_api)

/**
 * Заголовок раздела — ДОСЛОВНО пункт меню, по которому сюда пришли.
 *
 * Экран один на пять разделов, и своё название у каждого лежало в `SMART_TYPES.title`
 * рядом с номером типа — то есть во втором месте, независимом от меню. Два места разошлись
 * ровно там, где это заметнее всего: в меню «Договоры» и «АВР», в заголовке «Реестр
 * договоров» и «База АВР». Человек кликает по одному слову и видит другое, и первая мысль —
 * «я не туда попал».
 *
 * Поэтому источник названия теперь один — `NAV_GROUPS`. `spec.title` остаётся запасным
 * ответом на случай, когда пункта в меню нет вовсе: раздел, открытый по прямой ссылке
 * (или спрятанный правами), обязан показать заголовок, а не пустое место.
 */
function названиеРаздела(spec: SmartTypeSpec): string {
  const путь = `/smart/${spec.path}`
  for (const г of NAV_GROUPS) {
    for (const п of г.items) {
      if (п.kind === 'crm' && п.to === путь) return п.label
    }
  }
  return spec.title
}

export default function SmartProcess() {
  const { type } = useParams()
  const spec = smartTypeByPath(type)

  if (!spec) {
    return (
      <div className="card">
        <h1>Раздел не найден</h1>
        <p className="muted">
          Известные разделы смарт-процессов: {SMART_TYPES.map((t) => `/smart/${t.path}`).join(', ')}.
        </p>
      </div>
    )
  }
  // key — чтобы при переходе между типами не «переехали» чужие фильтры и колонки:
  // у другого типа других полей просто нет, и фильтр по коду из прошлого типа ничего не найдёт
  return <SmartList key={spec.typeId} spec={spec} />
}

function SmartList({ spec }: { spec: SmartTypeSpec }) {
  const { typeId } = spec
  const [sp, setSp] = useSearchParams()
  // Компания живёт в адресе: ссылку «счета этого клиента» должно быть можно переслать.
  const companyId = sp.get('company') || ''

  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('')
  const [stage, setStage] = useState('')
  const [assigned, setAssigned] = useState('')
  const [start, setStart] = useState(0)

  // фильтр по произвольному полю типа
  const [ffCode, setFfCode] = useState('')
  const [ffMode, setFfMode] = useState<FilterMode>('contains')
  const [ffV1, setFfV1] = useState('')
  const [ffV2, setFfV2] = useState('')

  const [sortField, setSortField] = useState('updatedTime')
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC')

  // null — «пользователь колонки не трогал», показываем автоподбор по справочнику
  const [pickedCols, setPickedCols] = useState<string[] | null>(null)

  const refs = useRefs()
  const f = useFields(smartEntity(typeId))
  /**
   * Тот же запрос, что внутри useFields (ключ кэша один) — нужен только его статус.
   *
   * Колонки этого раздела строятся по справочнику полей, а от колонок зависит `select`.
   * Спросить записи ДО прихода `/fields` значит сходить в ядро дважды: первый раз с одними
   * системными полями, второй — после пересборки колонок. Лишний запрос там не главная беда:
   * на первом ответе в таблице на мгновение видны данные без нужных колонок, то есть счёт
   * без суммы и без срока оплаты. Приём тот же, что в Client360 (`invFieldsPending`).
   */
  const fPending = useFieldsQuery(smartEntity(typeId)).isLoading
  // Название выбранной компании: один запрос на компанию (кэш на 10 минут), а не на строку.
  const companyTitle = useEntityTitle('company', companyId || undefined)
  const companyValue: EntityRef | null = companyId
    ? { kind: 'company', id: companyId, title: companyTitle.data || `#${companyId}` }
    : null

  const columns = useMemo(() => buildColumns(f, spec), [f, spec])
  const shownCodes = pickedCols ?? columns.defaults
  const shownCols = useMemo(
    () => shownCodes.map((c) => columns.byCode.get(c)).filter((c) => !!c),
    [shownCodes, columns],
  )

  const categories = useMemo(() => refs.itemCategories(typeId), [refs, typeId])
  const stageOptions = useMemo(() => refs.itemStageOptions(typeId, category), [refs, typeId, category])
  // Справочник передаём нарочно: без него в список сортировок попадали четыре поля
  // `lastCommunication*`, пустые во всех записях — выбор, который ничего не меняет.
  const sortOptions = useMemo(() => sortableColumns(columns.all, f), [columns, f])

  const ffCol = ffCode ? columns.byCode.get(ffCode) : undefined
  const ffModes = ffCol ? filterModes(ffCol.kind) : []
  const ffEnum = ffCol ? f.meta(ffCol.code)?.enum ?? [] : []

  const params = useMemo<ListParams>(() => {
    const filter: Record<string, unknown> = {}
    if (companyId) filter.companyId = companyId
    if (title.trim()) filter['%title'] = title.trim()
    if (category) filter.categoryId = category
    if (stage) filter.stageId = stage          // у смарт-процессов stageId всегда с префиксом
    if (assigned) filter.assignedById = assigned
    Object.assign(filter, fieldFilter(ffCode, ffMode, ffV1, ffV2))

    // Явный select: страница — 50 сырых записей, а полей у типа под сотню. Просим системные
    // (они дешёвые и нужны всегда) плюс то, что реально показано или отфильтровано.
    const select = [...new Set([...SYSTEM_SELECT, ...shownCodes, ...(ffCode ? [ffCode] : [])])]
    return { filter, order: { [sortField]: sortDir }, select, start }
  }, [companyId, title, category, stage, assigned, ffCode, ffMode, ffV1, ffV2,
      shownCodes, sortField, sortDir, start])

  const q = useItems(typeId, params, !fPending)
  const data = q.data
  const rows = data?.items ?? []
  const totals = useMemo(() => moneyTotals(shownCols, rows), [shownCols, rows])

  // смена любого фильтра — всегда с первой страницы
  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const changeCategory = (v: string) => {
    setStart(0)
    setCategory(v)
    // у другой воронки другие стадии — выбранная повисла бы и не нашла ничего
    if (v && stage && !refs.itemStageOptions(typeId, v).some((o) => o.id === stage)) setStage('')
  }

  const changeCompany = (v: EntityRef | null) => {
    setStart(0)
    const next = new URLSearchParams(sp)
    if (v) next.set('company', v.id)
    else next.delete('company')
    setSp(next, { replace: true })
  }

  const changeFfCode = (code: string) => {
    setStart(0)
    setFfCode(code)
    setFfV1('')
    setFfV2('')
    const col = columns.byCode.get(code)
    // режим прошлого поля другому типу может не подходить («диапазон» для перечисления)
    setFfMode(col ? filterModes(col.kind)[0] : 'contains')
  }

  const toggleCol = (code: string) => {
    const cur = new Set(shownCodes)
    if (cur.has(code)) cur.delete(code)
    else cur.add(code)
    // порядок колонок держим общий (системные, потом поля типа), а не порядок кликов
    setPickedCols(columns.all.filter((c) => cur.has(c.code)).map((c) => c.code))
  }

  return (
    <>
      <div className="page-head">
        <h1>{названиеРаздела(spec)}</h1>
        {/* «в вашей зоне» у самого числа: счётчик читают отдельно от оговорок ниже. */}
        <span className="muted">
          {data && !data.stale
            ? `записей: ${data.total}${data.scope?.limited ? ' (в вашей зоне)' : ''}`
            : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hint" style={{ marginBottom: 12 }}>
          {spec.note} Смарт-процесс {typeId}, раздел только читает.
        </div>

        {/* Откуда читается СПИСОК при источнике 'own' (умолчание переключателя,
            api/istochnik.ts). Оговорку считает api/smartOwn.ts тем же вызовом, которым
            транспорт выбирает источник, — подпись не может разойтись с делом. Когда
            человек явно вернул зеркало (?src=mirror или localStorage), блока нет вовсе:
            это привычное состояние раздела, и подписывать его значило бы приучить
            не читать оговорки. Раскрытие строки (все поля записи) и под own читает
            зеркало: перенесённых полей мало, а полная запись есть только там. */}
        <ИсточникРаздела typeId={typeId} params={params} />

        {/* Счета клиенту (31): почему тут нет кнопки «Печать договора-счёта».
            Вопрос задают, потому что в «Отгрузках» такая кнопка есть и работает
            (components/Print1cButton.tsx, форма «счёт»). Ответ — не «не успели», а «нечего
            печатать», и он стоит здесь с числами: пустой бланк счёта подпишут и отнесут
            в банк, а прочерк вместо состава читается как «состава нет». */}
        {typeId === 31 && <ПочемуНетПечатиСчёта />}

        {/* Ключ сворачивания — с номером типа: экран один на пять разделов, и «раскрыл
            фильтры в счетах» не должно раскрывать их в договорах. */}
        <FilterBar id={`smart-${typeId}`}>
          <div className="f" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="sp-title">Название содержит</label>
            <input id="sp-title" value={title}
                   onChange={(e) => resetAnd(setTitle)(e.target.value)} />
          </div>

          <div style={{ flex: 2, minWidth: 220 }}>
            <EntityPicker kind="company" id="sp-company" label="Компания"
                          value={companyValue} onChange={changeCompany} />
          </div>

          <div className="f">
            <label htmlFor="sp-cat">Воронка</label>
            <select id="sp-cat" value={category} onChange={(e) => changeCategory(e.target.value)}
                    disabled={categories.length === 0}>
              <option value="">любая</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="sp-stage">Стадия</label>
            <select id="sp-stage" value={stage} onChange={(e) => resetAnd(setStage)(e.target.value)}
                    disabled={stageOptions.length === 0}>
              <option value="">любая</option>
              {stageOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.categoryName ? `${o.name} · ${o.categoryName}` : o.name}
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="sp-resp">Ответственный</label>
            <select id="sp-resp" value={assigned}
                    onChange={(e) => resetAnd(setAssigned)(e.target.value)}
                    disabled={refs.users.length === 0}>
              <option value="">любой</option>
              {refs.users.map((u) => (
                <option key={u.id} value={u.id}>{u.active ? u.name : `${u.name} (уволен)`}</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="sp-sort">Сортировка</label>
            <select id="sp-sort" value={`${sortField}|${sortDir}`}
                    onChange={(e) => {
                      const [fld, dir] = e.target.value.split('|')
                      setStart(0)
                      setSortField(fld)
                      setSortDir(dir === 'ASC' ? 'ASC' : 'DESC')
                    }}>
              {sortOptions.flatMap((c) => [
                <option key={`${c.code}-d`} value={`${c.code}|DESC`}>{c.label} ↓</option>,
                <option key={`${c.code}-a`} value={`${c.code}|ASC`}>{c.label} ↑</option>,
              ])}
            </select>
          </div>
        </FilterBar>

        {/* Фильтр по любому полю типа: состав полей заранее неизвестен, поэтому вместо
            набора именованных фильтров — выбор поля из справочника.

            Раньше этот блок намеренно НЕ сворачивался: «пока поле не выбрано, это одна
            строка». Но как только поле выбрано, строк становится до четырёх, и на телефоне
            они закрывают собой список — то есть исключение работало ровно наоборот. Теперь
            блок сворачивается тем же механизмом, что и остальные, и свёрнутый перечисляет
            применённое в заголовке: скрытой причины из него не получается.

            Ключ памяти свой у каждого типа смарт-процесса: «Счета клиенту» и «Счета на
            оплату» — один экран, но разные разделы меню, и общий ключ схлопывал бы обоим
            разом. */}
        <FilterBar id={`smart-field-${typeId}`} style={{ marginTop: 12 }}>
          <div className="f" style={{ flex: 2, minWidth: 240 }}>
            <label htmlFor="sp-ff">Фильтр по полю</label>
            <select id="sp-ff" value={ffCode} onChange={(e) => changeFfCode(e.target.value)}
                    disabled={!f.loaded}>
              <option value="">— не выбрано —</option>
              {columns.all.filter((c) => !c.system).map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>

          {ffCol && (
            <div className="f">
              <label htmlFor="sp-ff-mode">Условие</label>
              <select id="sp-ff-mode" value={ffMode}
                      onChange={(e) => resetAnd(setFfMode)(e.target.value as FilterMode)}>
                {ffModes.map((m) => (
                  <option key={m} value={m}>{FILTER_MODE_LABEL[m]}</option>
                ))}
              </select>
            </div>
          )}

          {ffCol && ffMode === 'eq' && ffEnum.length > 0 && (
            <div className="f" style={{ minWidth: 200 }}>
              <label htmlFor="sp-ff-enum">Значение</label>
              <select id="sp-ff-enum" value={ffV1}
                      onChange={(e) => resetAnd(setFfV1)(e.target.value)}>
                <option value="">любое</option>
                {ffEnum.map((o) => <option key={o.id} value={o.id}>{o.value}</option>)}
              </select>
            </div>
          )}

          {ffCol && (ffMode === 'contains' || (ffMode === 'eq' && ffEnum.length === 0)) && (
            <div className="f" style={{ minWidth: 200 }}>
              <label htmlFor="sp-ff-v">Значение</label>
              <input id="sp-ff-v" value={ffV1} onChange={(e) => resetAnd(setFfV1)(e.target.value)} />
            </div>
          )}

          {ffCol && ffMode === 'range' && (
            <>
              <div className="f">
                <label htmlFor="sp-ff-from">От</label>
                <input id="sp-ff-from" value={ffV1}
                       type={ffCol.kind === 'number' ? 'number' : 'date'}
                       onChange={(e) => resetAnd(setFfV1)(e.target.value)} />
              </div>
              <div className="f">
                <label htmlFor="sp-ff-to">До</label>
                <input id="sp-ff-to" value={ffV2}
                       type={ffCol.kind === 'number' ? 'number' : 'date'}
                       onChange={(e) => resetAnd(setFfV2)(e.target.value)} />
              </div>
            </>
          )}
        </FilterBar>

        {ffCol?.kind === 'money' && (
          <div className="hint" style={{ marginTop: 10 }}>
            По денежному полю диапазон не предлагается: в ядре оно лежит строкой «515000|RUB»,
            и сравнение «больше/меньше» дало бы не ошибку, а молча пустой список.
          </div>
        )}

        <details style={{ marginTop: 12 }}>
          <summary className="hint" style={{ cursor: 'pointer' }}>
            Колонки: показано {shownCols.length} из {columns.all.length}
          </summary>
          <div style={{ marginTop: 10 }}>
            <div className="hint" style={{ marginBottom: 8 }}>
              Набор по умолчанию подобран по справочнику полей: системные колонки плюс первые
              поля со суммой, датой или списком значений. «Главные» поля типа из справочника
              не выводятся, поэтому выбор — за вами.
            </div>
            <div className="filters">
              {columns.all.map((c) => (
                <label key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                       title={`${c.code}${c.bpComputed ? ' · пишет бизнес-процесс' : ''}`}>
                  <input type="checkbox" style={{ width: 'auto' }}
                         checked={shownCodes.includes(c.code)}
                         onChange={() => toggleCol(c.code)} />
                  {c.label}
                  {!c.known && !c.system && <span className="muted"> (без названия)</span>}
                </label>
              ))}
            </div>
            <button type="button" style={{ marginTop: 10 }} onClick={() => setPickedCols(null)}>
              вернуть набор по умолчанию
            </button>
          </div>
        </details>

        {!f.loaded && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник полей смарт-процесса {typeId} из ядра не пришёл: колонок с полями типа
            нет (их состав неоткуда взять), фильтр по полю отключён. Показаны только системные
            колонки записи.
          </div>
        )}
        {f.loaded && f.stale && (
          <div className="hint" style={{ marginTop: 10 }}>
            Справочник полей давно не обновлялся из Bitrix — названия колонок могут отставать.
          </div>
        )}
        {!refs.hasItemStages(typeId) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Стадии смарт-процесса {typeId} из ядра не пришли: в таблице они показаны сырыми
            кодами Bitrix, фильтры по стадии и воронке отключены.
          </div>
        )}
        {refs.loaded && refs.dictsStale && (
          <div className="hint" style={{ marginTop: 10 }}>
            Воронки и стадии — из старого кеша ядра (Bitrix не ответил). Названия могут отставать.
          </div>
        )}
      </div>

      {/* Пока ждём справочник полей, запрос записей ещё не отправлен: у выключенного запроса
          isLoading=false, и без `fPending` на секунду мигало бы «Ничего не найдено». */}
      <QueryState isLoading={q.isLoading || fPending} error={q.error}
                  stale={data?.stale} ageMin={data?.ageMin} scope={data?.scope}>
        {totals.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="page-head" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Суммы по странице</h2>
              <div className="spacer" />
              <span className="hint">
                по показанным {rows.length}{' '}
                {plural(rows.length, 'строке', 'строкам', 'строкам')} из {data?.total ?? 0}
              </span>
            </div>
            <div className="tiles">
              {totals.map((t) => (
                <div key={`${t.code}-${t.currency}`} className="tile">
                  <div className="t-label">{t.label}</div>
                  <div className="t-sum">{fmtMoney(t.sum, t.currency)}</div>
                  <div className="t-label">{t.count} знач.</div>
                </div>
              ))}
            </div>
            {/* Ровно та же оговорка, что в дебиторке: агрегатов ядро не отдаёт, а выкачивать
                23 тысячи счетов ради итога — это 460 запросов. */}
            <div className="hint" style={{ marginTop: 10 }}>
              Это сумма ЗАГРУЖЕННОЙ страницы
              ({pluralN(rows.length, 'запись', 'записи', 'записей')}), а не всего раздела:
              агрегаты ядро пока не считает, оно отдаёт данные страницами по {PAGE}. Валюты
              не смешиваются — каждая своей плиткой.
            </div>
          </div>
        )}

        {/* На устаревших данных таблицы нет вовсе: под пометкой «данным верить нельзя»
            «Ничего не найдено под эти фильтры» читалось бы как «таких записей нет».
            То же правило, что в дебиторке и в списке сделок. */}
        {data?.stale ? (
          <div className="card">
            <div className="muted">
              Список не показан: ядро предупредило, что данные устарели. Пустая таблица здесь
              означала бы «таких записей нет», а мы этого сейчас не знаем. Обновите страницу,
              когда синк догонит (состояние синка — в разделе «Состояние ядра»).
            </div>
          </div>
        ) : (
        <div className="card">
          <SmartItemsTable typeId={typeId} rows={rows} cols={shownCols}
                           emptyText={data?.scope?.limited
                             // «в вашей зоне таких записей нет» и «таких записей нет» — разные
                             // утверждения; во втором человек ищет дальше, в первом — идёт
                             // к тому, у кого зона шире.
                             ? 'В вашей зоне таких записей нет — выборка сужена правами, а не фильтрами'
                             : 'Ничего не найдено под эти фильтры'} />

          {data && data.total > PAGE && (
            <div className="pager">
              <button disabled={start === 0 || q.isFetching}
                      onClick={() => setStart(Math.max(0, start - PAGE))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + PAGE, data.total)} из {data.total}
              </span>
              <button disabled={data.next === undefined || q.isFetching}
                      onClick={() => setStart(data.next!)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
        )}
      </QueryState>

      <div className="card">
        <h2>Почему здесь только чтение</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          {spec.note} Правка таких записей перезапускает бизнес-процессы Bitrix: они двигают
          стадии, рассылают задания и пересчитывают поля. Поля, помеченные значком ⚙,
          не станут редактируемыми и после появления записи в CRM — их значение затрёт робот.
        </p>
        <BpFields typeId={typeId} />
      </div>
    </>
  )
}

/**
 * Почему из счетов клиенту (31) договор-счёт не печатается.
 *
 * Числа — из запросов к зеркалу `cir_core` 28.07.2026, не по памяти:
 *   • `deal_products` — единственная таблица состава в зеркале, и в ней только
 *     `owner_type='D'`: 98 560 строк по 31 491 сделке, по счетам — 0;
 *   • `crm.item.productrow.list` в белом списке `ЧТЕНИЕ` (web/bff/rights.py) отсутствует,
 *     значит и живьём из Bitrix строки счёта фронту не прочитать;
 *   • счетов типа 31 в зеркале 31 463; сделка привязана у 24 617, у 6 846 её нет вовсе;
 *     из привязанных состав сделки есть у 18 646, и у части из них сумма состава сделки
 *     не сходится с суммой счёта (сделку делят на несколько счетов, аванс и доплату).
 *     СКОЛЬКО ИМЕННО — зависит от формулы: 4 391 получается при `price_brutto*quantity`,
 *     а ядро (`products_api`) считает `price*quantity` и даёт 4 107. Разница между
 *     формулами больше, чем суточный дрейф данных, поэтому число здесь не приводится:
 *     смотрите раздел «Расхождения состава», он считает по формуле ядра;
 *   • КПП покупателя в зеркале нет НИ У ОДНОЙ из 10 683 компаний, а юридический адрес
 *     есть у 22 — то есть «ни у одной» верно только про КПП. Реквизиты Bitrix
 *     (`crm.requisite`) не зеркалятся; 22 адреса лежат прямо в карточке компании;
 *   • сумма НДС (`taxValue`) заполнена у 1 358 счетов из 31 463.
 *
 * Кнопка, печатающая бланк с пустой таблицей и прочерками вместо реквизитов, хуже
 * отсутствующей: пустой лист подписывают и несут в банк.
 */
function ПочемуНетПечатиСчёта() {
  return (
    <div className="hint" style={{ marginBottom: 12 }}>
      <b>Печати договора-счёта здесь нет, и это не забытая кнопка.</b> Бланк счёта в CRM уже
      работает — в «Отгрузках», по документу 1С. Он печатает таблицу товаров и работ, а у
      счетов этого типа состава в зеркале нет вовсе: строки состава Bitrix хранит отдельно,
      и зеркалятся они только по сделкам, по счетам — НИ ОДНОЙ строки; метода
      <code>crm.item.productrow.list</code> в белом списке BFF тоже нет. Взять состав из
      связанной сделки нельзя: у части счетов сделки нет вовсе, а там, где она есть, состав
      сделки часто расходится с суммой счёта — сделку делят на
      аванс и доплату. Сверх того: КПП покупателя в зеркале нет ни у одной компании, а
      юридический адрес есть у считаных единиц — реквизиты Bitrix не зеркалятся; сумма НДС
      заполнена у малой доли счетов. Счёт с пустой таблицей и прочерком вместо реквизитов
      в банк не примут — печатать такой хуже, чем не печатать.
    </div>
  )
}

/**
 * Оговорка об источнике списка. Отдельным компонентом, чтобы не трогать разметку
 * SmartList: текст (или его отсутствие) целиком решает api/smartOwn.оговоркаИсточника.
 */
function ИсточникРаздела({ typeId, params }: { typeId: number; params: ListParams }) {
  const текст = оговоркаИсточника(typeId, params)
  if (!текст) return null
  return <div className="hint" style={{ marginBottom: 12 }}>{текст}</div>
}

/** Поля типа, которые считает бизнес-процесс: их видно в справочнике, и о них честно сказано. */
function BpFields({ typeId }: { typeId: number }) {
  const f = useFields(smartEntity(typeId))
  const rows = useMemo(
    () => f.list.filter((x) => x.meta.bp_computed || x.meta.readonly).slice(0, 40),
    [f],
  )

  if (!f.loaded) {
    return (
      <div className="hint">
        Справочник полей ядра недоступен — какие поля пишет бизнес-процесс, сказать нечем.
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="hint">
        В справочнике ядра у этого типа нет полей с пометкой «только чтение» или «считает
        бизнес-процесс». Это не разрешение писать: раздел читающий целиком.
      </div>
    )
  }
  return (
    <dl className="dl">
      {rows.map(({ code, meta }) => (
        <div key={code} style={{ display: 'contents' }}>
          <dt title={`${code} · тип ${meta.type} (${kindOfFieldType(meta.type)})`}>
            {meta.title && meta.title !== code ? meta.title : code}
          </dt>
          <dd>
            <span className="badge lose">
              только чтение{meta.bp_computed ? ': значение считает бизнес-процесс' : ''}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  )
}
